// src/Dashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import { useAuth } from './AuthContext';
import { getAllItems, loadUserSingleDoc } from './firestore';

// Icons
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PaymentsIcon from '@mui/icons-material/Payments';
import PaidIcon from '@mui/icons-material/Paid';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// --------------------
// Premium constants
// --------------------
const ACCENT = '#2BB673'; // mint green accent
const ACCENT_SOFT = 'rgba(43, 182, 115, 0.10)';
const BORDER = 'rgba(17, 24, 39, 0.10)';
const TEXT_MUTED = 'rgba(17, 24, 39, 0.65)';

// Button style (premium)
const ModernButton = styled(Button)({
  borderRadius: '12px',
  padding: '10px 18px',
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': {
    transform: 'translateY(-1px)',
    transition: 'all 0.15s ease-in-out',
    boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
  },
});

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().split('T')[0];

const inSameMonth = (isoDate, year, monthIndex0) => {
  if (!isoDate) return false;
  const d = new Date(isoDate);
  return d.getFullYear() === year && d.getMonth() === monthIndex0;
};

// --------------------
// Stat Card (premium)
// --------------------
const StatCard = ({
  label,
  value,
  sub,
  icon,
  accent = false,
  tone = 'neutral', // neutral | success | warning | danger
}) => {
  const toneStyles = {
    neutral: {
      iconBg: 'rgba(17, 24, 39, 0.06)',
      iconColor: 'rgba(17, 24, 39, 0.70)',
      valueColor: 'rgba(17, 24, 39, 0.92)',
      topBar: 'rgba(17, 24, 39, 0.10)',
    },
    success: {
      iconBg: 'rgba(34, 197, 94, 0.12)',
      iconColor: 'rgb(22, 163, 74)',
      valueColor: 'rgb(22, 163, 74)',
      topBar: 'rgba(34, 197, 94, 0.35)',
    },
    warning: {
      iconBg: 'rgba(245, 158, 11, 0.14)',
      iconColor: 'rgb(217, 119, 6)',
      valueColor: 'rgba(17, 24, 39, 0.92)',
      topBar: 'rgba(245, 158, 11, 0.38)',
    },
    danger: {
      iconBg: 'rgba(239, 68, 68, 0.12)',
      iconColor: 'rgb(220, 38, 38)',
      valueColor: 'rgb(220, 38, 38)',
      topBar: 'rgba(239, 68, 68, 0.35)',
    },
  };

  const t = toneStyles[tone] || toneStyles.neutral;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        p: 2.2,
        borderRadius: 3,
        minWidth: 220,
        bgcolor: 'white',
        border: `1px solid ${BORDER}`,
        overflow: 'hidden',
        transition: 'transform 140ms ease, box-shadow 140ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 18px 40px rgba(0,0,0,0.10)',
        },
      }}
    >
      {/* Top accent bar */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          height: 4,
          width: '100%',
          bgcolor: accent ? ACCENT : t.topBar,
        }}
      />

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: accent ? ACCENT_SOFT : t.iconBg,
            color: accent ? ACCENT : t.iconColor,
            flex: '0 0 auto',
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
            {label}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              mt: 0.5,
              fontWeight: 900,
              letterSpacing: '-0.02em',
              color: accent ? ACCENT : t.valueColor,
              lineHeight: 1.05,
            }}
          >
            {value}
          </Typography>

          {sub ? (
            <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
              {sub}
            </Typography>
          ) : (
            <Box sx={{ height: 16 }} />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// --------------------
// Action Card (premium)
// Entire card clickable
// --------------------
const ActionCard = ({ title, description, to, icon, accent = false }) => (
  <Paper
    component={Link}
    to={to}
    elevation={0}
    sx={{
      textDecoration: 'none',
      color: 'inherit',
      p: 3,
      borderRadius: 4,
      bgcolor: 'white',
      width: { xs: '100%', sm: 360 },
      border: `1px solid ${BORDER}`,
      transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 18px 40px rgba(0,0,0,0.10)',
        borderColor: accent ? 'rgba(43, 182, 115, 0.45)' : BORDER,
      },
    }}
  >
    {/* tiny accent glow (only for primary action) */}
    {accent && (
      <Box
        sx={{
          position: 'absolute',
          inset: -60,
          background: `radial-gradient(circle at 20% 20%, rgba(43,182,115,0.10), transparent 60%)`,
          pointerEvents: 'none',
        }}
      />
    )}

    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', position: 'relative' }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: accent ? ACCENT_SOFT : 'rgba(17, 24, 39, 0.06)',
          color: accent ? ACCENT : 'rgba(17, 24, 39, 0.70)',
          flex: '0 0 auto',
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 900, letterSpacing: '-0.01em' }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 2 }}>
          {description}
        </Typography>

        <ModernButton
          variant={accent ? 'contained' : 'outlined'}
          sx={{
            borderColor: accent ? 'transparent' : 'rgba(17, 24, 39, 0.16)',
            bgcolor: accent ? ACCENT : 'transparent',
            color: accent ? 'white' : 'rgba(17, 24, 39, 0.92)',
            '&:hover': {
              bgcolor: accent ? '#24A667' : 'rgba(17, 24, 39, 0.04)',
              borderColor: accent ? 'transparent' : 'rgba(17, 24, 39, 0.22)',
            },
          }}
        >
          Start
        </ModernButton>
      </Box>
    </Box>
  </Paper>
);

const Section = ({ title, right, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
      {right ? <Box>{right}</Box> : null}
    </Box>

    {/* keep this WHITE, but with subtle border + hover-ready softness */}
    <Box
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 4,
        border: `1px solid ${BORDER}`,
        bgcolor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(6px)',
      }}
    >
      {children}
    </Box>
  </Box>
);

const Dashboard = () => {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();

  const [dataLoading, setDataLoading] = useState(true);

  const [participants, setParticipants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [settings, setSettings] = useState({ taxRate: 0.3 });

  useEffect(() => {
    if (loading) return;
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadStats = async () => {
      try {
        const [p, inv, ag, te, st] = await Promise.all([
          getAllItems(currentUser.uid, 'participants'),
          getAllItems(currentUser.uid, 'invoices'),
          getAllItems(currentUser.uid, 'agreements'),
          getAllItems(currentUser.uid, 'timeEntries'),
          loadUserSingleDoc(currentUser.uid, 'settings', { taxRate: 0.3 }),
        ]);

        setParticipants(p || []);
        setInvoices(inv || []);
        setAgreements(ag || []);
        setTimeEntries(te || []);
        setSettings(st || { taxRate: 0.3 });
      } catch (error) {
        console.error('Dashboard stats error:', error);
      } finally {
        setDataLoading(false);
      }
    };

    loadStats();
  }, [currentUser, loading, navigate]);

  const insights = useMemo(() => {
    const activeParticipants = (participants || []).filter((p) => !p.archived);

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOf7Days = new Date(startOfToday);
    endOf7Days.setDate(endOf7Days.getDate() + 7);

    const upcomingShifts = (timeEntries || [])
      .filter((t) => t?.date)
      .filter((t) => {
        const d = new Date(t.date);
        return d >= startOfToday && d < endOf7Days;
      });

    const isInvoiced = (t) => !!(t?.invoiceId || t?.invoicedAt || t?.invoiced === true);

    const uninvoicedEntries = (timeEntries || []).filter((t) => !isInvoiced(t));
    const uninvoicedHours = uninvoicedEntries.reduce((sum, t) => sum + Number(t?.hours || 0), 0);

    const todayIso = todayISO();
    const overdueInvoices = (invoices || []).filter((inv) => {
      const due = inv?.dueDate;
      if (!due) return false;
      const isPaid = !!inv?.paid;
      return !isPaid && due < todayIso;
    });

    const now = new Date();
    const y = now.getFullYear();
    const m0 = now.getMonth();

    const billedThisMonth = (invoices || [])
      .filter((inv) => inSameMonth(inv?.date, y, m0))
      .reduce((sum, inv) => sum + Number(inv?.totalAmount || 0), 0);

    const paidThisMonth = (invoices || [])
      .filter((inv) => !!inv?.paid && inSameMonth(inv?.paidAt || inv?.date, y, m0))
      .reduce((sum, inv) => sum + Number(inv?.totalAmount || 0), 0);

    const unpaidTotal = (invoices || [])
      .filter((inv) => !inv?.paid)
      .reduce((sum, inv) => sum + Number(inv?.totalAmount || 0), 0);

    const taxRate = Number(settings?.taxRate ?? 0.3);
    const setAsideThisMonth = paidThisMonth * taxRate;

    return {
      activeParticipants: activeParticipants.length,
      invoicesCount: (invoices || []).length,
      agreementsCount: (agreements || []).length,
      shiftsCount: (timeEntries || []).length,

      upcomingShiftsCount: upcomingShifts.length,
      uninvoicedHours,
      overdueInvoicesCount: overdueInvoices.length,
      billedThisMonth,
      paidThisMonth,
      unpaidTotal,

      taxRate,
      setAsideThisMonth,
    };
  }, [participants, invoices, agreements, timeEntries, settings]);

  if (loading || dataLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  const taxPct = Math.round((insights.taxRate || 0.3) * 100);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#ffffff', // ✅ keep it clean next to the dark sidebar
        position: 'relative',
      }}
    >
      {/* ultra-subtle mint glow (small, not a full wash) */}
      <Box
        sx={{
          position: 'absolute',
          top: -120,
          right: -140,
          width: 420,
          height: 420,
          background: 'radial-gradient(circle, rgba(43,182,115,0.16), transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ maxWidth: '1200px', mx: 'auto', p: { xs: 2, md: 3 }, position: 'relative' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 950, letterSpacing: '-0.02em' }}>
              Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
              Overview for today{currentUser?.email ? ` • ${currentUser.email}` : ''}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<AccountBalanceWalletIcon sx={{ color: ACCENT }} />}
              label={`Tax rate: ${taxPct}%`}
              sx={{
                bgcolor: 'white',
                border: `1px solid ${BORDER}`,
                '& .MuiChip-label': { fontWeight: 800 },
              }}
            />

            {insights.overdueInvoicesCount > 0 && (
              <Chip
                icon={<WarningAmberIcon />}
                label={`${insights.overdueInvoicesCount} overdue`}
                color="error"
                variant="filled"
                sx={{ '& .MuiChip-label': { fontWeight: 800 } }}
              />
            )}
          </Stack>
        </Box>

        <Section
          title="Today / This week"
          right={<Typography variant="body2" sx={{ color: TEXT_MUTED }}>Focus: what needs attention next</Typography>}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StatCard
              label="Upcoming shifts (7 days)"
              value={insights.upcomingShiftsCount}
              sub="Based on time tracking dates"
              icon={<EventAvailableIcon fontSize="small" />}
              accent
            />
            <StatCard
              label="Uninvoiced hours"
              value={Number(insights.uninvoicedHours || 0).toFixed(2)}
              sub="Entries not linked to an invoice yet"
              icon={<AccessTimeIcon fontSize="small" />}
              tone={insights.uninvoicedHours > 0 ? 'warning' : 'neutral'}
            />
            <StatCard
              label="Overdue invoices"
              value={insights.overdueInvoicesCount}
              sub="Unpaid past due date"
              icon={<ErrorOutlineIcon fontSize="small" />}
              tone={insights.overdueInvoicesCount > 0 ? 'danger' : 'neutral'}
            />
          </Box>
        </Section>

        <Section
          title="Money snapshot (this month)"
          right={<Typography variant="body2" sx={{ color: TEXT_MUTED }}>Quick view of cash in vs outstanding</Typography>}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StatCard
              label="Billed this month"
              value={money(insights.billedThisMonth)}
              sub="Invoices issued this month"
              icon={<PaymentsIcon fontSize="small" />}
              accent
            />
            <StatCard
              label="Paid this month"
              value={money(insights.paidThisMonth)}
              sub="Paid invoices this month"
              icon={<PaidIcon fontSize="small" />}
              tone="success"
            />
            <StatCard
              label="Suggested tax to set aside"
              value={money(insights.setAsideThisMonth)}
              sub={`${taxPct}% of paid income`}
              icon={<AccountBalanceWalletIcon fontSize="small" />}
              tone="warning"
            />
            <StatCard
              label="Unpaid total (all time)"
              value={money(insights.unpaidTotal)}
              sub="All unpaid invoices"
              icon={<WarningAmberIcon fontSize="small" />}
              tone={insights.unpaidTotal > 0 ? 'danger' : 'neutral'}
            />
          </Box>
        </Section>

        <Divider sx={{ my: 3, borderColor: 'rgba(17,24,39,0.10)' }} />

        <Section
          title="At a glance"
          right={<Typography variant="body2" sx={{ color: TEXT_MUTED }}>Core totals across your workspace</Typography>}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StatCard label="Active Participants" value={insights.activeParticipants} icon={<GroupIcon fontSize="small" />} />
            <StatCard label="Invoices" value={insights.invoicesCount} icon={<ReceiptLongIcon fontSize="small" />} />
            <StatCard label="Service Agreements" value={insights.agreementsCount} icon={<ReceiptLongIcon fontSize="small" />} />
            <StatCard label="Logged Shifts" value={insights.shiftsCount} icon={<AccessTimeIcon fontSize="small" />} />
          </Box>
        </Section>

        <Section
          title="Quick actions"
          right={<Typography variant="body2" sx={{ color: TEXT_MUTED }}>Jump straight to what you do most</Typography>}
        >
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <ActionCard
              title="Create Invoice"
              description="Create an invoice with NDIS billing codes and download a PDF."
              to="/invoices"
              icon={<ReceiptLongIcon />}
              accent
            />
            <ActionCard
              title="Log Shift"
              description="Track support time and generate a shift report."
              to="/time-tracking"
              icon={<AccessTimeIcon />}
            />
            <ActionCard
              title="Participants"
              description="Manage participants, plan dates, billing preferences, goals and contacts."
              to="/participants"
              icon={<GroupIcon />}
            />
          </Box>
        </Section>

        <Box sx={{ mt: 4, mb: 1 }}>
          <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
            Tip: keep “Uninvoiced hours” at zero to stay on top of billing.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;





